import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Plug, Plus, Key, Copy, Trash2, Loader2, CheckCircle, Eye, EyeOff,
  Code, ExternalLink, Clock, Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { GHLConnectPanel } from "@/components/ghl/GHLConnectPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardIntegrations() {
  const { getAuthHeader } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [keyName, setKeyName] = useState("Default");
  const [newKey, setNewKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    try {
      const res = await axios.get(`${API}/integrations/api-keys`, { headers: getAuthHeader() });
      setKeys(res.data?.keys || []);
    } catch (err) { console.error("Failed to fetch API keys:", err); }
    finally { setLoading(false); }
  };

  const createKey = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/integrations/api-keys`, { name: keyName }, { headers: getAuthHeader() });
      setNewKey(res.data.key);
      toast.success("API key created! Copy it now — it won't be shown again.");
      setKeyName("Default");
      await fetchKeys();
    } catch (err) { toast.error("Failed to create key"); }
    finally { setCreating(false); }
  };

  const revokeKey = async (keyId) => {
    try {
      await axios.delete(`${API}/integrations/api-keys/${keyId}`, { headers: getAuthHeader() });
      toast.success("Key revoked");
      await fetchKeys();
    } catch (err) { toast.error("Failed to revoke"); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-integrations">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Integrations & API</h1>
        <p className="text-[#94A3B8] mt-1">Connect bots and external services to manage your listings programmatically</p>
      </div>

      {/* GoHighLevel CRM Connection */}
      <GHLConnectPanel />

      {/* New Key Display */}
      {newKey && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-[#10B981] font-semibold mb-2 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> API Key Created</p>
              <p className="text-sm text-[#94A3B8] mb-3">Copy this key now. It will not be shown again.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-black/30 rounded-lg text-[#10B981] font-mono text-sm break-all">{newKey}</code>
                <button onClick={() => copyText(newKey)} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 text-white" data-testid="copy-new-key">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button onClick={() => setNewKey(null)} className="text-[#64748B] hover:text-white mt-1">
              <EyeOff className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Create Key */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-[#7C3AED]" />
          API Keys
        </h2>
        <div className="flex gap-3 mb-6">
          <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Key name (e.g. Manus Bot)"
            className="bg-white/5 border-white/10 text-white max-w-xs" data-testid="api-key-name" />
          <Button onClick={createKey} disabled={creating} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="create-api-key-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Generate Key
          </Button>
        </div>

        {keys.length > 0 ? (
          <div className="space-y-2">
            {keys.map((key) => (
              <div key={key.key_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <Key className="w-4 h-4 text-[#7C3AED]" />
                  <div>
                    <p className="text-white text-sm font-medium">{key.name}</p>
                    <p className="text-xs text-[#64748B] font-mono">{key.key_prefix}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#64748B]">
                  <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> {key.usage_count || 0} calls</span>
                  {key.last_used_at && (
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(key.last_used_at).toLocaleDateString()}</span>
                  )}
                  <button onClick={() => revokeKey(key.key_id)} className="p-1.5 rounded hover:bg-red-500/10 text-[#64748B] hover:text-red-400" data-testid={`revoke-${key.key_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#64748B]">No API keys yet. Create one to get started.</p>
        )}
      </motion.div>

      {/* API Documentation */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Code className="w-5 h-5 text-[#10B981]" />
            API Documentation
          </h2>
          <button onClick={() => setShowDocs(!showDocs)} className="text-sm text-[#7C3AED] hover:text-[#8B5CF6]" data-testid="toggle-docs">
            {showDocs ? "Hide" : "Show Endpoints"}
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-[#94A3B8] mb-2">Base URL:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-black/30 rounded-lg text-[#7C3AED] font-mono text-sm">{BASE_URL}/api/v1/integrations</code>
            <button onClick={() => copyText(`${BASE_URL}/api/v1/integrations`)} className="p-2 bg-white/10 rounded-lg hover:bg-white/20 text-white">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-[#64748B] mt-2">All requests require header: <code className="text-[#F59E0B]">X-API-Key: your_api_key</code></p>
        </div>

        {showDocs && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 border-t border-white/10 pt-4">
            {[
              { method: "GET", path: "/listings", desc: "List all your business listings" },
              { method: "POST", path: "/listings", desc: "Create a new business listing", body: '{"name": "My Business", "city": "Chicago", "state": "IL", "status": "draft"}' },
              { method: "PATCH", path: "/listings/{slug}", desc: "Update a listing by slug" },
              { method: "POST", path: "/listings/{slug}/publish", desc: "Publish a listing" },
              { method: "GET", path: "/inventory", desc: "List marketplace inventory" },
              { method: "POST", path: "/inventory", desc: "Add inventory item", body: '{"listing_type": "vehicles", "title": "2024 Tesla Model 3", "make": "Tesla", "model": "Model 3", "year": 2024, "price": 35000}' },
            ].map((ep, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${
                    ep.method === "GET" ? "bg-[#3B82F6]/20 text-[#3B82F6]" :
                    ep.method === "POST" ? "bg-[#10B981]/20 text-[#10B981]" :
                    "bg-[#F59E0B]/20 text-[#F59E0B]"
                  }`}>{ep.method}</span>
                  <code className="text-white text-sm font-mono">{ep.path}</code>
                </div>
                <p className="text-xs text-[#94A3B8]">{ep.desc}</p>
                {ep.body && (
                  <pre className="mt-2 px-3 py-2 bg-black/30 rounded text-xs text-[#94A3B8] font-mono overflow-x-auto">{ep.body}</pre>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
